import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useProgramRegisters,
  useProgramRegisterDetail,
  type ProgramItem,
} from "@/lib/program-registration";
import { BookOpen, ChevronRight } from "lucide-react";

// ── Field options ─────────────────────────────────────────────────────────────

const FIELD_OPTIONS = [
  { key: "plannedContent" as const,             label: "Planned content" },
  { key: "plannedResources" as const,           label: "Resources" },
  { key: "outcomeOrContentDotPoint" as const,   label: "Outcomes / content dot points" },
  { key: "plannedAssessmentCheckpoint" as const, label: "Assessment / checkpoint" },
];

const ALL_FIELD_KEYS = FIELD_OPTIONS.map((f) => f.key);

// ── Status display ────────────────────────────────────────────────────────────

const ITEM_STATUS_LABELS: Record<string, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  partially_delivered: "Partially delivered",
  postponed: "Postponed",
  delivered_as_planned: "Completed",
  modified_during_delivery: "Modified",
  not_delivered_due_to_interruption: "Not delivered",
  replaced_with_alternate_content: "Replaced",
};

const ITEM_STATUS_CLASSES: Record<string, string> = {
  not_started:                      "bg-gray-100 text-gray-600",
  in_progress:                      "bg-blue-100 text-blue-700",
  partially_delivered:              "bg-amber-100 text-amber-700",
  postponed:                        "bg-gray-100 text-gray-500",
  delivered_as_planned:             "bg-green-100 text-green-700",
  modified_during_delivery:         "bg-purple-100 text-purple-700",
  not_delivered_due_to_interruption: "bg-red-100 text-red-700",
  replaced_with_alternate_content:  "bg-violet-100 text-violet-700",
};

// ── HTML builder ──────────────────────────────────────────────────────────────

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join("<br>");
}

function buildInsertHtml(item: ProgramItem, fields: string[]): string {
  const parts: string[] = [];
  for (const { key, label } of FIELD_OPTIONS) {
    if (!fields.includes(key)) continue;
    const val = item[key] as string | null;
    if (!val?.trim()) continue;
    parts.push(`<h3>${label}</h3><p>${escapeHtml(val)}</p>`);
  }
  return parts.join("");
}

// ── Component ─────────────────────────────────────────────────────────────────

interface ProgramItemPickerProps {
  open: boolean;
  onClose: () => void;
  onInsert: (html: string) => void;
  /** Called with the selected register + item after insertion, so the
   *  caller can track which items have been pulled into the current session. */
  onPickItem?: (registerId: number, item: ProgramItem) => void;
}

export function ProgramItemPicker({ open, onClose, onInsert, onPickItem }: ProgramItemPickerProps) {
  const [selectedRegisterId, setSelectedRegisterId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<ProgramItem | null>(null);
  const [selectedFields, setSelectedFields] = useState<string[]>(ALL_FIELD_KEYS);

  const { data: registers = [], isLoading: loadingRegisters } = useProgramRegisters();
  const activeRegisters = registers.filter((r) => r.status !== "archived");

  const { data: detail, isLoading: loadingItems } = useProgramRegisterDetail(
    selectedRegisterId ?? 0
  );

  const sortedItems = (detail?.items ?? []).slice().sort(
    (a, b) => (a.plannedOrder ?? 0) - (b.plannedOrder ?? 0)
  );

  function handleClose() {
    setSelectedRegisterId(null);
    setSelectedItem(null);
    setSelectedFields(ALL_FIELD_KEYS);
    onClose();
  }

  function handleInsert() {
    if (!selectedItem || !selectedRegisterId) return;
    const html = buildInsertHtml(selectedItem, selectedFields);
    if (!html.trim()) return;
    onInsert(html);
    onPickItem?.(selectedRegisterId, selectedItem);
    handleClose();
  }

  function toggleField(key: string) {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  }

  const canInsert =
    selectedItem !== null &&
    selectedFields.some((f) => {
      const val = selectedItem[f as keyof ProgramItem] as string | null;
      return val?.trim();
    });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl flex flex-col max-h-[90vh]">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            Pull from program
          </DialogTitle>
          <DialogDescription>
            Choose a Program Item to insert planned content, resources and outcomes into your lesson plan. This does not mark the item as delivered.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-5 py-1">
          {/* Step 1: Register selector */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Program register</label>
            {loadingRegisters ? (
              <Skeleton className="h-9 w-full" />
            ) : activeRegisters.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No active program registers found.</p>
            ) : (
              <Select
                value={selectedRegisterId ? String(selectedRegisterId) : ""}
                onValueChange={(v) => {
                  setSelectedRegisterId(Number(v));
                  setSelectedItem(null);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a program register…" />
                </SelectTrigger>
                <SelectContent>
                  {activeRegisters.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      <span className="font-medium">{r.title}</span>
                      {(r.subject || r.yearGroup) && (
                        <span className="ml-2 text-xs text-gray-400">
                          {[r.subject, r.yearGroup].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Step 2: Item list */}
          {selectedRegisterId !== null && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Program item</label>
              {loadingItems ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full rounded-md" />)}
                </div>
              ) : sortedItems.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-4 text-center border rounded-md">
                  No items in this register yet.
                </p>
              ) : (
                <div className="border rounded-md divide-y divide-gray-100">
                    {sortedItems.map((item) => {
                      const isSelected = selectedItem?.id === item.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setSelectedItem(isSelected ? null : item)}
                          className={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors hover:bg-gray-50 ${
                            isSelected
                              ? "bg-emerald-50 border-l-[3px] border-l-emerald-500"
                              : "border-l-[3px] border-l-transparent"
                          }`}
                        >
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {item.plannedWeek && (
                                <span className="text-[11px] text-gray-400 flex-shrink-0">
                                  Wk {item.plannedWeek}
                                </span>
                              )}
                              <span className="text-sm font-medium text-gray-800 truncate">
                                {item.title}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium flex-shrink-0 ${
                                  ITEM_STATUS_CLASSES[item.overallStatus] ?? "bg-gray-100 text-gray-500"
                                }`}
                              >
                                {ITEM_STATUS_LABELS[item.overallStatus] ?? item.overallStatus}
                              </span>
                              {item.plannedContent && (
                                <span className="text-xs text-gray-400 truncate">
                                  {item.plannedContent.slice(0, 70)}
                                  {item.plannedContent.length > 70 ? "…" : ""}
                                </span>
                              )}
                            </div>
                          </div>
                          <ChevronRight
                            className={`h-4 w-4 flex-shrink-0 mt-0.5 transition-colors ${
                              isSelected ? "text-emerald-600" : "text-gray-300"
                            }`}
                          />
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Field checkboxes */}
          {selectedItem !== null && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-3">
              <p className="text-sm font-medium text-gray-700">Fields to insert</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
                {FIELD_OPTIONS.map(({ key, label }) => {
                  const val = selectedItem[key] as string | null;
                  const hasVal = !!val?.trim();
                  return (
                    <label
                      key={key}
                      className={`flex items-start gap-3 select-none ${
                        hasVal ? "cursor-pointer" : "cursor-not-allowed opacity-40"
                      }`}
                    >
                      <Checkbox
                        checked={selectedFields.includes(key) && hasVal}
                        onCheckedChange={() => { if (hasVal) toggleField(key); }}
                        disabled={!hasVal}
                        className="mt-0.5 h-5 w-5 flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <span className="text-sm text-gray-700 font-medium">{label}</span>
                        {hasVal ? (
                          <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]">
                            {val!.slice(0, 55)}{val!.length > 55 ? "…" : ""}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-400 mt-0.5">Not set on this item</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleInsert}
            disabled={!canInsert}
            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Insert into diary
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
